import { AlertCircle, Check, X } from 'lucide-react';

import { useEffect, useMemo, useState } from 'react';

import {
  PASSWORD_REQUIREMENTS,
  type PasswordRequirementKey,
  getUnmetPasswordRequirements,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { cn } from '@/lib/utils';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
  key: string;
}

/** Maps each shared policy requirement to its i18n label key. */
const REQUIREMENT_LABEL_KEYS: Record<PasswordRequirementKey, string> = {
  length: 'common:forms.atLeastChars',
  uppercase: 'common:forms.oneUppercase',
  lowercase: 'common:forms.oneLowercase',
  number: 'common:forms.oneNumber',
  special: 'common:forms.oneSpecialChar',
};

interface PasswordStrengthIndicatorProps {
  password: string;
  confirmPassword?: string;
  showConfirmMatch?: boolean;
  className?: string;
  compact?: boolean;
}

export function PasswordStrengthIndicator({
  password,
  confirmPassword = '',
  showConfirmMatch = false,
  className,
  compact = false,
}: PasswordStrengthIndicatorProps) {
  const [touched, setTouched] = useState(false);
  const { t } = useTranslation();

  // Requirements come from the shared policy (single source of truth, matches
  // Cognito); we only map each requirement key to its localized label.
  const requirements: PasswordRequirement[] = useMemo(
    () =>
      PASSWORD_REQUIREMENTS.map(req => ({
        key: req.key,
        label: t(REQUIREMENT_LABEL_KEYS[req.key]),
        test: req.test,
      })),
    [t]
  );

  // Calculate password strength
  const { percentage, color, bgColor, label } = useMemo(() => {
    if (!password) {
      return {
        strength: 0,
        percentage: 0,
        color: 'text-gray-400',
        bgColor: 'bg-gray-200 dark:bg-gray-700',
        label: t('common:forms.enterPassword'),
      };
    }

    const passed = requirements.filter(req => req.test(password)).length;
    const percentage = (passed / requirements.length) * 100;

    if (percentage === 0) {
      return {
        strength: 0,
        percentage: 0,
        color: 'text-red-500',
        bgColor: 'bg-red-500',
        label: t('common:forms.strengthVeryWeak'),
      };
    } else if (percentage < 40) {
      return {
        strength: 1,
        percentage,
        color: 'text-red-500',
        bgColor: 'bg-red-500',
        label: t('common:forms.strengthWeak'),
      };
    } else if (percentage < 60) {
      return {
        strength: 2,
        percentage,
        color: 'text-orange-500',
        bgColor: 'bg-orange-500',
        label: t('common:forms.strengthFair'),
      };
    } else if (percentage < 80) {
      return {
        strength: 3,
        percentage,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-500',
        label: t('common:forms.strengthGood'),
      };
    } else if (percentage < 100) {
      return {
        strength: 4,
        percentage,
        color: 'text-green-500',
        bgColor: 'bg-green-500',
        label: t('common:forms.strengthStrong'),
      };
    } else {
      return {
        strength: 5,
        percentage: 100,
        color: 'text-green-600',
        bgColor: 'bg-green-600',
        label: t('common:forms.strengthVeryStrong'),
      };
    }
  }, [password, requirements, t]);

  // Check if passwords match
  const passwordsMatch = useMemo(() => {
    if (!showConfirmMatch || !confirmPassword || !password) return null;
    return password === confirmPassword;
  }, [password, confirmPassword, showConfirmMatch]);

  // Mark as touched once user starts typing
  // Use separate effect to avoid setting state directly in response to password change
  useEffect(() => {
    if (password) {
      // Wrap setState in setTimeout to avoid direct state setting in effect
      const timer = setTimeout(() => {
        setTouched(true);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [password]);

  // Don't show anything if password is empty and not touched
  if (!password && !touched) {
    return null;
  }

  if (compact) {
    // Compact mode: just show the strength bar with label
    return (
      <div
        className={cn('space-y-2', className)}
        data-testid="password-strength-indicator-compact"
      >
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {t('common:forms.passwordStrength')}
          </span>
          <span
            className={cn('text-xs font-semibold', color)}
            data-testid="password-strength-label"
          >
            {label}
          </span>
        </div>
        <div className="relative">
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-300 ease-out rounded-full',
                bgColor
              )}
              style={{ width: `${percentage}%` }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Password strength: ${label}`}
              data-testid="password-strength-bar"
            />
          </div>
        </div>
      </div>
    );
  }

  // Full mode: show detailed requirements
  return (
    <div
      className={cn('space-y-3', className)}
      data-testid="password-strength-indicator"
    >
      {/* Strength indicator bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('common:forms.passwordStrength')}
          </span>
          <span
            className={cn('text-sm font-semibold', color)}
            data-testid="password-strength-label"
          >
            {label}
          </span>
        </div>
        <div className="relative">
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-300 ease-out rounded-full',
                bgColor
              )}
              style={{ width: `${percentage}%` }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Password strength: ${label}`}
              data-testid="password-strength-bar"
            />
          </div>
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1.5" data-testid="password-requirements">
        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {t('common:forms.passwordRequirements')}
        </p>
        <ul className="space-y-1" role="list">
          {requirements.map(req => {
            const passed = req.test(password);
            return (
              <li
                key={req.key}
                className={cn(
                  'flex items-center gap-2 text-xs transition-colors duration-200',
                  passed
                    ? 'text-green-600 dark:text-green-400'
                    : password
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-gray-500 dark:text-gray-400'
                )}
                role="listitem"
                data-testid={`password-requirement-${req.key}`}
              >
                <span
                  className={cn(
                    'shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-all duration-200',
                    passed
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : password
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-gray-100 dark:bg-gray-800'
                  )}
                >
                  {passed ? (
                    <Check className="w-2.5 h-2.5" aria-hidden="true" />
                  ) : password ? (
                    <X className="w-2.5 h-2.5" aria-hidden="true" />
                  ) : (
                    <span className="w-1.5 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                  )}
                </span>
                <span
                  className={cn(
                    'transition-opacity duration-200',
                    passed ? 'opacity-100' : 'opacity-75'
                  )}
                >
                  {req.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Password match indicator */}
      {showConfirmMatch && confirmPassword && (
        <div
          className={cn(
            'flex items-center gap-2 text-xs p-2 rounded-md transition-all duration-200',
            passwordsMatch
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
          )}
          data-testid="password-match-indicator"
        >
          {passwordsMatch ? (
            <>
              <Check className="w-3.5 h-3.5 shrink-0" />
              <span data-testid="password-match-message">
                {t('common:forms.passwordsMatch')}
              </span>
            </>
          ) : (
            <>
              <X className="w-3.5 h-3.5 shrink-0" />
              <span data-testid="password-mismatch-message">
                {t('common:forms.passwordsDoNotMatch')}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Validate a password against the shared policy. `errors` are i18n KEYS for
// the unmet requirements (in policy order) — callers localize at display time
// (e.g. `t(errors[0])`) so nothing here hardcodes English.
export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors = getUnmetPasswordRequirements(password).map(
    key => REQUIREMENT_LABEL_KEYS[key]
  );
  return {
    isValid: errors.length === 0,
    errors,
  };
}
